import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getErrorMessage } from '@/utils/errors';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon, Edit as EditIcon } from '@mui/icons-material';
import { teamApi } from '@/services/api';
import { CompetencyMatrix } from '@/types';

interface ExpertsTableProps {
  teamId: string;
  onNotify: (message: string, severity: 'success' | 'error') => void;
}

type KnowledgeLevel = 'high' | 'medium' | 'low';
type Availability = 'full-time' | 'part-time';

const EMPTY_FORM = {
  expert_name: '',
  expert_email: '',
  expert_tag: '',
  google_chat_user_id: '',
  expertise: '',
  keywords: [] as string[],
  knowledge_level: 'medium' as KnowledgeLevel,
  availability: 'full-time' as Availability,
};

export default function ExpertsTable({ teamId, onNotify }: ExpertsTableProps) {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<CompetencyMatrix | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [newKeyword, setNewKeyword] = useState('');

  const { data: experts } = useQuery({
    queryKey: ['team-experts', teamId],
    queryFn: () => teamApi.getExperts(teamId).then((res) => res.data),
    enabled: !!teamId,
  });

  const addMutation = useMutation({
    mutationFn: (data: Partial<CompetencyMatrix>) => teamApi.addExpert(teamId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-experts', teamId] });
      onNotify('Expert added successfully!', 'success');
      handleClose();
    },
    onError: (error: Error) => {
      onNotify(`Failed to add expert: ${getErrorMessage(error)}`, 'error');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CompetencyMatrix> }) =>
      teamApi.updateExpert(teamId, id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-experts', teamId] });
      onNotify('Expert updated successfully!', 'success');
      handleClose();
    },
    onError: (error: Error) => {
      onNotify(`Failed to update expert: ${getErrorMessage(error)}`, 'error');
    },
  });

  const removeExpertMutation = useMutation({
    mutationFn: (expertId: string) => teamApi.removeExpert(teamId, expertId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-experts', teamId] });
      onNotify('Expert removed successfully!', 'success');
    },
    onError: (error: Error) => {
      onNotify(`Failed to remove expert: ${getErrorMessage(error)}`, 'error');
    },
  });

  const handleOpen = (item?: CompetencyMatrix) => {
    if (item) {
      setEditingItem(item);
      setForm({
        expert_name: item.expert_name,
        expert_email: item.expert_email || '',
        expert_tag: item.expert_tag || '',
        google_chat_user_id: item.google_chat_user_id || '',
        expertise: item.expertise || '',
        keywords: item.keywords || [],
        knowledge_level: item.knowledge_level || 'medium',
        availability: item.availability || 'full-time',
      });
    } else {
      setEditingItem(null);
      setForm(EMPTY_FORM);
    }
    setNewKeyword('');
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setEditingItem(null);
    setForm(EMPTY_FORM);
    setNewKeyword('');
  };

  const handleSave = () => {
    const data: Partial<CompetencyMatrix> = {
      expert_name: form.expert_name,
      expert_email: form.expert_email || undefined,
      expert_tag: form.expert_tag || undefined,
      google_chat_user_id: form.google_chat_user_id || undefined,
      expertise: form.expertise || undefined,
      keywords: form.keywords,
      knowledge_level: form.knowledge_level,
      availability: form.availability,
    };
    if (editingItem) {
      updateMutation.mutate({ id: editingItem.id, data });
    } else {
      addMutation.mutate(data);
    }
  };

  const handleRemove = (expertId: string) => {
    if (window.confirm('Are you sure you want to remove this expert?')) {
      removeExpertMutation.mutate(expertId);
    }
  };

  const handleAddKeyword = () => {
    const kw = newKeyword.trim();
    if (kw && !form.keywords.includes(kw)) {
      setForm((prev) => ({ ...prev, keywords: [...prev.keywords, kw] }));
      setNewKeyword('');
    }
  };

  const handleRemoveKeyword = (keyword: string) => {
    setForm((prev) => ({ ...prev, keywords: prev.keywords.filter((k) => k !== keyword) }));
  };

  const isSaving = addMutation.isPending || updateMutation.isPending;

  return (
    <>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Configure team experts with their Google Chat User IDs to enable @mention tagging in bot responses.
          To get a user's Google Chat ID, check your Google Admin Console or use the Chat API.
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpen()}
          sx={{ ml: 2, whiteSpace: 'nowrap' }}
        >
          Add Expert
        </Button>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Expert</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Google Chat ID</TableCell>
              <TableCell>Keywords</TableCell>
              <TableCell>Level</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {experts?.map((expert) => (
              <TableRow key={expert.id}>
                <TableCell>{expert.expert_name}</TableCell>
                <TableCell>{expert.expert_email || '-'}</TableCell>
                <TableCell>
                  <Typography
                    variant="body2"
                    fontFamily="monospace"
                    color={expert.google_chat_user_id ? 'text.primary' : 'text.disabled'}
                  >
                    {expert.google_chat_user_id || 'Not configured'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {expert.keywords.slice(0, 3).map((kw, i) => (
                      <Chip key={i} label={kw} size="small" variant="outlined" />
                    ))}
                    {expert.keywords.length > 3 && (
                      <Chip label={`+${expert.keywords.length - 3}`} size="small" />
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={expert.knowledge_level}
                    size="small"
                    color={
                      expert.knowledge_level === 'high'
                        ? 'success'
                        : expert.knowledge_level === 'medium'
                        ? 'warning'
                        : 'default'
                    }
                  />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit expert">
                    <IconButton size="small" onClick={() => handleOpen(expert)} sx={{ mr: 0.5 }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Remove expert">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemove(expert.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {experts?.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No experts added yet
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{editingItem ? 'Edit Expert' : 'Add Expert'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Expert Name"
              value={form.expert_name}
              onChange={(e) => setForm((prev) => ({ ...prev, expert_name: e.target.value }))}
              required
              fullWidth
            />
            <TextField
              label="Email"
              type="email"
              value={form.expert_email}
              onChange={(e) => setForm((prev) => ({ ...prev, expert_email: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Expert Tag"
              value={form.expert_tag}
              onChange={(e) => setForm((prev) => ({ ...prev, expert_tag: e.target.value }))}
              fullWidth
              helperText="Display tag for the expert (e.g. @john)"
            />
            <TextField
              label="Google Chat User ID"
              value={form.google_chat_user_id}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, google_chat_user_id: e.target.value }))
              }
              fullWidth
              helperText="e.g. users/123456789 — enables clickable @mentions"
            />
            <TextField
              label="Expertise"
              value={form.expertise}
              onChange={(e) => setForm((prev) => ({ ...prev, expertise: e.target.value }))}
              fullWidth
              multiline
              rows={2}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Knowledge Level</InputLabel>
                <Select
                  value={form.knowledge_level}
                  label="Knowledge Level"
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, knowledge_level: e.target.value as KnowledgeLevel }))
                  }
                >
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Availability</InputLabel>
                <Select
                  value={form.availability}
                  label="Availability"
                  onChange={(e) => setForm((prev) => ({ ...prev, availability: e.target.value as Availability }))}
                >
                  <MenuItem value="full-time">Full-time</MenuItem>
                  <MenuItem value="part-time">Part-time</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box>
              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <TextField
                  label="Add Keyword"
                  value={newKeyword}
                  onChange={(e) => setNewKeyword(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddKeyword();
                    }
                  }}
                  size="small"
                  fullWidth
                />
                <Button variant="outlined" onClick={handleAddKeyword} disabled={!newKeyword.trim()}>
                  Add
                </Button>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {form.keywords.map((kw, i) => (
                  <Chip key={i} label={kw} size="small" onDelete={() => handleRemoveKeyword(kw)} />
                ))}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={
              isSaving || !form.expert_name.trim() || form.keywords.length === 0
            }
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
